import { mediaManagerServerApi } from './mediaManagerServerApi';

/** Everything that narrows what TV mode plays. */
export interface ITvModeChannel {
  includeSeen: boolean;
  kinds: string;
  genres?: string[];
  tags?: string[];
}

/**
 * The selection as a query string, built once for all four TV mode
 * endpoints.
 *
 * Shared rather than written out at each one because every one of them
 * tops the queue up server-side, so a call that leaves part of the
 * selection out quietly undoes it - which is exactly what reordering
 * did with the kinds before it was noticed. One place to get right.
 *
 * Genres and tags repeat the parameter, ?genre=Action&genre=Comedy,
 * which is how the catalogs' filters send theirs.
 */
export const tvModeQuery = ({
  includeSeen,
  kinds,
  genres = [],
  tags = [],
}: ITvModeChannel) => {
  const parts = [
    `include_seen=${includeSeen ? 'true' : 'false'}`,
    `kinds=${encodeURIComponent(kinds)}`,
    ...genres.map((genre) => `genre=${encodeURIComponent(genre)}`),
    ...tags.map((tag) => `tag=${encodeURIComponent(tag)}`),
  ];
  return `?${parts.join('&')}`;
};

export const extendedApiSlice = mediaManagerServerApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccessLevel: builder.query<any, void>({
      query: () => `authentication/access-level/`,
      // Not cached: it is the ceiling an administrator set, and being
      // shown a stale one would offer a level the server then refuses.
      keepUnusedDataFor: 0,
    }),

    setAccessLevel: builder.mutation<any, any>({
      query: (accessLevel: number) => ({
        url: `authentication/access-level/`,
        method: 'PATCH',
        body: { access_level: accessLevel },
      }),
    }),

    getRandomPick: builder.query<any, any>({
      // mode=home asks the serializers for the handful of fields a
      // modal needs - the same way every other endpoint here picks its
      // shape. unseen is only sent when it is on, since autographs
      // have nothing to narrow by.
      query: ({ kind, unseen }: { kind: string; unseen?: boolean }) => {
        const paths: Record<string, string> = {
          movie: 'movie/movies',
          tvShow: 'tv_show/tv-shows',
          autograph: 'autograph/autographs',
        };
        const unseenPart = unseen ? '&unseen=true' : '';
        return `${paths[kind]}/random_pick/?mode=home${unseenPart}`;
      },
      // Never cached: asking again is the whole point, and a cached
      // answer would hand back the same item every time.
      keepUnusedDataFor: 0,
    }),

    getTvModeQueue: builder.query<any, any>({
      query: (channel: ITvModeChannel) => `core/tv-mode/${tvModeQuery(channel)}`,
      // Reading it tops the queue up server-side, so a fresh read is
      // also how new library content gets in. Never served from cache.
      keepUnusedDataFor: 0,
      // Tagged so that finishing, skipping or dragging refreshes what
      // is on screen. Triggering the query again by hand does not:
      // with the same arguments it settles without re-rendering, and
      // the page went on showing the item it had just moved past.
      providesTags: ['TvModeQueue'],
    }),

    tvModeItemPlayed: builder.mutation<any, any>({
      query: ({ id, ...channel }: { id: number } & ITvModeChannel) => ({
        url: `core/tv-mode/${id}/played/${tvModeQuery(channel)}`,
        method: 'POST',
      }),
      invalidatesTags: ['TvModeQueue'],
    }),

    reorderTvModeQueue: builder.mutation<any, any>({
      query: ({ itemIds, ...channel }: { itemIds: number[] } & ITvModeChannel) => ({
        url: `core/tv-mode/reorder/${tvModeQuery(channel)}`,
        method: 'POST',
        body: { item_ids: itemIds },
      }),
      invalidatesTags: ['TvModeQueue'],
    }),

    rebuildTvModeQueue: builder.mutation<any, any>({
      // Sent whenever the selection changes. Reading the queue is not
      // enough: a read only tops it up, so switching back to both while
      // twenty films are already waiting would add no episodes at all.
      //
      // Deliberately invalidates nothing. The read that follows is the
      // one the change of selection triggers by itself, with the new
      // selection in its arguments - invalidating here would instead
      // refetch the old one and undo the redraw.
      query: (channel: ITvModeChannel) => ({
        url: `core/tv-mode/rebuild/${tvModeQuery(channel)}`,
        method: 'POST',
      }),
    }),

    getTvModeFilterOptions: builder.query<any, void>({
      query: () => `core/tv-mode/filter_options/`,
      // The lists arrive as {id, label}; react-select wants a value as
      // well. Shaped here exactly as the catalogs shape theirs, and the
      // value is the name because that is what the filter is sent as.
      transformResponse: (raw: any) => {
        const toOptions = (items: any[] = []) =>
          items.map((item: any) => ({ value: item.label, label: item.label }));
        return {
          genres: toOptions(raw?.genres),
          tags: toOptions(raw?.tags),
        };
      },
    }),

    getContinueWatching: builder.query<any, void>({
      query: () => `core/continue-watching/`,
      // Refetched whenever the profile page is opened rather than
      // served from cache: the whole point of the section is where you
      // left off, and watching something in another tab or on the TV
      // changes it without this app hearing about it.
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useLazyGetTvModeQueueQuery,
  useTvModeItemPlayedMutation,
  useReorderTvModeQueueMutation,
  useRebuildTvModeQueueMutation,
  useGetTvModeFilterOptionsQuery,
  useLazyGetRandomPickQuery,
  useGetContinueWatchingQuery,
  useGetAccessLevelQuery,
  useSetAccessLevelMutation,
} = extendedApiSlice;
